import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { disposeApi } from "@/api";
import * as Profile from "@/components/auth/Profile";
import { ChatUnreadMessagesCount } from "@/components/chat/ChatUnreadMessagesCount";
import { TOP_MENU_BUTTON_CLASS_NAME } from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { useCreateHintHoverHandlers, useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import * as Skeleton from "@/components/skeleton/Skeleton";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { docPageUrl, teamPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";
import { getLastDocIdForTeam } from "@/utils/useLastDocId";

import { UnreadDot } from "../UnreadDot";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { TeamModel } from "@/models/TeamModel";
import type { MenuItems } from "@headlessui/react";
import type { ComponentProps } from "react";

type TeamItemProps = {
  team: TeamModel;
  onClick?: () => void;
  onMoreClick?: () => void;
  className?: string;
};

export function TeamItem({ team, onClick, onMoreClick, className, ...props }: TeamItemProps) {
  const t = useTranslations();
  const hintHandlers = useHintHover("teamSettings");

  return (
    <Sidebar.Button
      {...props}
      aria-label={team.title}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={twMerge(
        "relative pr-10 min-h-11 bg-block-bg",
        "sm:pr-12 sm:min-h-10",
        "data-[focus]:bg-sidebar-button-hover-bg",
        className,
      )}
      onClick={onClick}
      as="div"
    >
      <Profile.Userpic
        src={team.avatarUrls?.[64]}
        name={team.title || ""}
        setColorFrom={team.uid}
        className={twMerge("-my-1.25 w-[34px] h-[34px]", "sm:w-10 sm:h-10")}
      />
      <div className="flex flex-1 ml-1.5 min-w-0">
        <div className="flex-1 overflow-hidden">
          <div
            className={twMerge("flex min-w-0 gap-1 text-[15px] leading-[1.1]", "sm:text-[17px]")}
          >
            <span className="truncate font-semibold">{team.title}</span>
          </div>
          <div className="flex min-w-0 gap-1 text-gray-500 text-[13px] leading-[1.1]">
            <span className="truncate">@{team.permalink}</span>
            {team.personal ? (
              <span className="text-blue-500 shrink-0">{`· ${t("teams.personal")}`}</span>
            ) : (
              team.membersCount > 0 && (
                <span className="shrink-0">
                  {`· ${t("teams.membersCount", { count: team.membersCount })}`}
                </span>
              )
            )}
          </div>
        </div>
        <ChatUnreadMessagesCount className="m-auto ml-3.5" count={team.unreadMessagesCount} />
      </div>
      {onMoreClick && (
        <button
          aria-label="More"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onMoreClick();
          }}
          className={twMerge(
            "absolute top-1/2 -translate-y-1/2 right-0 flex mr-1.5 p-0",
            "!rounded-4 !min-h-0 !min-w-0 w-8 h-8 my-auto",
            "hover:bg-blue-100 hover:dark:bg-blue-900 ",
            "sm:w-10 sm:h-10 sm:hover:bg-transparent sm:dark:hover:bg-transparent",
          )}
          {...hintHandlers}
        >
          <Icon name="Settings" className="m-auto w-[24px] h-[24px] sm:w-[28px] sm:h-[28px]" />
        </button>
      )}
    </Sidebar.Button>
  );
}

function CreateTeamButton(props: ComponentProps<"button">) {
  const { dialog } = useStore();
  const t = useTranslations();

  return (
    <Sidebar.Button
      {...props}
      type="button"
      hintKey="createTeam"
      className={twMerge(
        "relative min-h-11",
        "sm:min-h-10",
        "data-[focus]:bg-sidebar-button-hover-bg",
      )}
      onClick={() => dialog.createTeamDialog.open()}
      as="button"
    >
      <div className="absolute -top-1.5 left-2.5 right-2.5 pointer-events-none" />
      <div
        className={twMerge("flex flex-1 items-center text-[16px] tracking-tight", "sm:text-[17px]")}
      >
        <Icon name="CreateTeamPlaceholder" className="w-[32px] h-[32px]" />
        <span className="ml-2">{t("teams.createTeam")}</span>
      </div>
    </Sidebar.Button>
  );
}

const CreateTeamButtonObserved = observer(CreateTeamButton);

const JoinCourseButton = observer(function JoinCourseButton() {
  const { studentOnboarding } = useStore();
  const t = useTranslations();

  return (
    <Sidebar.Button
      type="button"
      className={twMerge(
        "relative min-h-11",
        "sm:min-h-10",
        "data-[focus]:bg-sidebar-button-hover-bg",
      )}
      onClick={() => void studentOnboarding.openJoinCourse()}
    >
      <div
        className={twMerge("flex flex-1 items-center text-[16px] tracking-tight", "sm:text-[17px]")}
      >
        <Icon name="UniversityFlag" className="w-[24px] h-[24px] mx-1" />
        <span className="ml-2">{t("teams.joinCourse")}</span>
      </div>
    </Sidebar.Button>
  );
});

const TeamAvatar = observer(function TeamAvatar({
  team,
  className,
  personalTeam,
}: {
  team?: TeamModel;
  className?: string;
  personalTeam?: TeamModel;
}) {
  return (
    <div className="relative shrink-0 -my-1.25">
      <Profile.Userpic
        src={team?.avatarUrls?.[64]}
        name={team?.title || ""}
        className={twMerge(
          "text-size-18 w-[30px] h-[30px]",
          "sm:w-[30px] sm:h-[30px]",
          !team && "opacity-40",
          className,
        )}
        setColorFrom={team?.uid}
      />
      {personalTeam && team && personalTeam.uid !== team.uid && (
        <Profile.Userpic
          src={personalTeam.avatarUrls?.[48]}
          name={personalTeam.title}
          setColorFrom={personalTeam.uid}
          className={twMerge(
            "text-size-10 w-[18px] h-[18px] absolute -bottom-[3px] -right-[3px]",
            "sm:w-[18px] sm:h-[18px] shadow-[0_0_0_1px_var(--color-block-bg)]",
          )}
        />
      )}
    </div>
  );
});

export const TeamName = observer(function TeamName({ team }: { team?: TeamModel }) {
  return (
    <>
      <TeamAvatar team={team} />
      <div
        className={twMerge(
          "flex-1 ml-1.5 truncate text-left text-size-15 leading-[18px] font-semibold tracking-tight",
          "sm:text-[17px]",
        )}
      >
        {team ? team.title : <Skeleton.Text className="w-[150px]" />}
      </div>
    </>
  );
});

const SettingsButton = observer(() => {
  const t = useTranslations();
  const { dialog, teams } = useStore();

  return (
    <Sidebar.Button
      type="button"
      className={twMerge(
        "relative min-h-11",
        "sm:min-h-10",
        "data-[focus]:bg-sidebar-button-hover-bg",
      )}
      onClick={() => dialog.teamDialog.open(teams.personalTeam)}
    >
      <div
        className={twMerge("flex flex-1 items-center text-[16px] tracking-tight", "sm:text-[17px]")}
      >
        <Icon name="Settings" className="w-[24px] h-[24px] mx-1" />
        <span className="ml-2">{t("teamDialog.settings")}</span>
      </div>
    </Sidebar.Button>
  );
});

const ToggleButton = observer(
  ({
    avatarOnly,
    withArrow,
    collapsed,
  }: {
    avatarOnly?: boolean;
    withArrow?: boolean;
    collapsed?: boolean;
  }) => {
    const { teams } = useStore();
    const unreadMessagesCount = teams.userTeams.reduce(
      (acc, team) => acc + team.unreadMessagesCount,
      0,
    );
    const showUnreadMessagesIndicator = unreadMessagesCount > 0;
    const createHintHandlers = useCreateHintHoverHandlers();
    const { handlerProps: tooltipHandlerProps, tooltip } = useTooltip({
      title: avatarOnly ? (teams.currentTeam?.title ?? null) : null,
      placement: "right",
    });

    return (
      <div
        {...createHintHandlers("teams")}
        {...tooltipHandlerProps}
        className={twMerge(
          "relative flex flex-row items-center justify-center rounded-6 h-[38px] p-1.25",
          "text-sm tracking-tight",
          "cursor-pointer hover:bg-sidebar-button-hover-bg group-data-[focus]:bg-sidebar-button-hover-bg sm:hover:bg-transparent sm:dark:hover:bg-transparent",
          avatarOnly && !collapsed && twMerge(TOP_MENU_BUTTON_CLASS_NAME, "pl-[3px] pr-1"),
          avatarOnly &&
            "group-data-[open]/team-toggle:bg-blue-100 dark:group-data-[open]/team-toggle:bg-blue-900",
        )}
      >
        {avatarOnly ? (
          <>
            <TeamAvatar
              team={teams.currentTeam}
              personalTeam={teams.personalTeam}
              className="w-8 h-8 sm:w-8 sm:h-8"
            />
            {withArrow && teams.currentTeam && <Icon name="ArrowDown" className="ml-1 shrink-0" />}
          </>
        ) : (
          <>
            <TeamName team={teams.currentTeam} />
            {withArrow && teams.currentTeam && (
              <Icon
                name="ArrowDownLarge"
                width={16}
                height={16}
                className="ml-1.5 [&>path]:stroke-document-text"
              />
            )}
          </>
        )}
        {showUnreadMessagesIndicator && (
          <UnreadDot
            className={twMerge("absolute top-[9px] right-0", avatarOnly && "top-[2px] right-[2px]")}
          />
        )}
        {tooltip}
      </div>
    );
  },
);

export const TeamDropdown = observer(function TeamDropdown({
  avatarOnly,
  anchor = { to: "bottom start", padding: "10px" },
  withArrow,
  collapsed,
  className,
}: {
  avatarOnly?: boolean;
  anchor?: ComponentProps<typeof MenuItems>["anchor"];
  withArrow?: boolean;
  /** Rendered in the collapsed rail rather than a full-width top menu row */
  collapsed?: boolean;
  className?: string;
}) {
  const { teams, dialog, auth, studentOnboarding } = useStore();
  const t = useTranslations();
  const router = useRouter();
  const isSignedIn = Boolean(auth.userData);
  const { isAffiliationKnown } = studentOnboarding;

  useEffect(() => {
    if (!isSignedIn || isAffiliationKnown) return undefined;

    return disposeApi(async () => {
      await studentOnboarding.loadAffiliation();
    });
  }, [isSignedIn, isAffiliationKnown, studentOnboarding]);

  const handleTeamClick = (team: TeamModel) => {
    teams.setCurrentTeam(team);

    const lastDocId = getLastDocIdForTeam(team.uid);

    if (lastDocId) {
      router.replace(docPageUrl({ docId: lastDocId }));
    } else {
      router.replace(teamPageUrl({ teamPermalink: team.permalink }));
    }
  };

  const items: DropdownItem[] = [
    ...teams.sortedUserTeams.map((team) => ({
      title: team.title || t("teams.unnamed"),
      component: (
        <TeamItem
          team={team}
          onClick={() => handleTeamClick(team)}
          onMoreClick={() => {
            dialog.teamDialog.open(team);
          }}
          className={twMerge(teams.currentTeam?.uid === team.uid && "bg-blue-50 dark:bg-blue-950")}
        />
      ),
    })),
    {
      title: t("teams.createTeam"),
      component: <CreateTeamButtonObserved />,
    },
    {
      type: "separator",
    },
    {
      title: t("teams.joinCourse"),
      component: <JoinCourseButton />,
    },
    {
      type: "separator",
    },
    {
      title: t("teamDialog.settings"),
      component: <SettingsButton />,
    },
    {
      type: "separator",
    },
    {
      title: t("userMenu.signOut"),
      onClick: () => {
        auth.signOut();
      },
      className: twMerge("h-11 text-[16px]", "sm:h-[42px]", "hover:!bg-sidebar-button-hover-bg"),
    },
  ];

  return (
    <div className={twMerge("flex max-w-[160px]", avatarOnly && "max-w-full", className)}>
      <Dropdown
        anchor={anchor}
        className="max-w-full group/team-toggle"
        itemsClassName="fromSm:!max-w-[500px]"
        toggleButtonComponent={
          <ToggleButton avatarOnly={avatarOnly} withArrow={withArrow} collapsed={collapsed} />
        }
        toggleButtonLabel={t("teams.label")}
        items={items}
      />
    </div>
  );
});
